import { type Api } from "api-client";
import { type Questionnaire } from "api-client/legacy-models";
import { sort } from "fast-sort";
import { makeAutoObservable } from "mobx";
import { assert, groupBy, mapBy } from "utils";

export class QuestionnaireSubmissionStore {
  constructor() {
    makeAutoObservable(this);
  }

  optimisticAnswers: Record<string, Api.GetSubmission.Output.Answer> = {};

  _questionnaireSubmission: Api.GetSubmission.Output.Self | null = null;
  _submitSubmissionResult: Questionnaire.SubmitSubmission.Self | null = null;

  setSubmitSubmissionResult(result: Questionnaire.SubmitSubmission.Self) {
    this._submitSubmissionResult = result;
  }

  setQuestionnaireSubmission(
    questionnaireSubmission: Api.GetSubmission.Output.Self,
  ) {
    this._questionnaireSubmission = questionnaireSubmission;
  }

  hasErrorQuestion(questionId: string) {
    if (
      this._submitSubmissionResult &&
      "requiredItemsWithoutAnswer" in this._submitSubmissionResult
    ) {
      return this._submitSubmissionResult.requiredItemsWithoutAnswer.includes(
        questionId,
      );
    }

    return false;
  }

  get questionnaireSubmission() {
    assert(
      this._questionnaireSubmission,
      "Questionnaire submission is not loaded",
    );

    return this._questionnaireSubmission;
  }

  get navigation() {
    return this.questionnaireSubmission?.navigation;
  }

  get wasCreatedBySameUser() {
    return this.questionnaireSubmission.wasCreatedBySameUser;
  }

  get questionnaire() {
    return this.questionnaireSubmission.questionnaire;
  }

  get itemsById() {
    return mapBy(
      [...this.questionnaire.items, ...this.questionnaireSubmission.items],
      (x) => x.id,
    );
  }

  get questionsById() {
    return mapBy(
      Object.values(this.itemsById)
        .map((x) => x.question)
        .filter(Boolean),
      (x) => x.id,
    );
  }

  get items() {
    const items = this.isSubmitted
      ? this.questionnaireSubmission.items
      : this.questionnaire.items;

    return items;
  }

  get rootItems() {
    return sort(this.items.filter((item) => !item.parentItemId)).asc("order");
  }

  get optionsById() {
    return mapBy(
      Object.values(this.questionsById).flatMap((x) => x.options ?? []),
      (x) => x.id,
    );
  }

  get answersById() {
    return mapBy(this.questionnaireSubmission.answers, (x) => x.id);
  }

  get answersByQuestionId() {
    return mapBy(this.questionnaireSubmission.answers, (x) => x.questionId);
  }

  get allAnswersByQuestionId() {
    return mapBy(
      [
        ...this.questionnaireSubmission.answers,
        ...Object.values(this.optimisticAnswers),
      ],
      (x) => x.questionId,
    );
  }

  get itemsByParentItemId() {
    const grouped = groupBy(
      this.items.filter((item) => item.parentItemId),
      (e) => String(e.parentItemId),
    );

    Object.keys(grouped).forEach((key) => {
      grouped[key] = sort(grouped[key]).asc("order");
    });

    return grouped;
  }

  getItemsByItemId(itemId: string) {
    return this.itemsByParentItemId[itemId] || [];
  }

  addAnswer(answer: Api.GetSubmission.Output.Answer) {
    this.questionnaireSubmission.answers.push(answer);
  }

  createOptimisticAnswer(answer: Api.GetSubmission.Output.Answer) {
    this.optimisticAnswers[answer.questionId] = answer;
  }

  updateAnswerByQuestionId(
    questionId: string,
    answer: Partial<Api.GetSubmission.Output.Answer>,
  ) {
    const foundAnswer = this.allAnswersByQuestionId[questionId];
    Object.assign(foundAnswer, answer);
  }

  onAnswered(answer: Api.GetSubmission.Output.Answer) {
    if (this.answersById[answer.id]) {
      return;
    }

    const optimisticAnswer = this.optimisticAnswers[answer.questionId];

    if (optimisticAnswer) {
      this.addAnswer({
        ...optimisticAnswer,
        id: answer.id,
      });
      delete this.optimisticAnswers[answer.questionId];
    } else {
      this.addAnswer(answer);
    }
  }

  getQuestionOptionsAnswer(questionId: string) {
    const options = this.allAnswersByQuestionId[questionId]?.options || [];

    return options.map((o) => o.id);
  }

  getQuestionValueAnswer(questionId: string) {
    return this.allAnswersByQuestionId[questionId]?.value || "";
  }

  getQuestionConfirmedAnswer(questionId: string) {
    return this.allAnswersByQuestionId[questionId]?.confirmed || false;
  }

  get isSubmitted() {
    return this.questionnaireSubmission.submittedAt !== null;
  }
}
